import { z } from 'zod';
import { apiClient } from '../client';

/**
 * Tenant-wide settings (those that don't fit on a Customer).
 * ADR-031: today this is the coaching prompt + context docs that
 * CoachingResolver reads off the tenant self-link.
 */

const CoachingSchema = z.object({
  coaching_system_prompt: z.string().default(''),
  coaching_context_docs: z.string().default(''),
});

export type TenantCoaching = z.infer<typeof CoachingSchema>;

export async function getTenantCoaching(opts?: { signal?: AbortSignal }): Promise<TenantCoaching> {
  const raw = await apiClient.get<unknown>('/tenant/coaching', opts);
  return CoachingSchema.parse(raw);
}

export async function updateTenantCoaching(
  input: Partial<TenantCoaching>,
): Promise<TenantCoaching> {
  const raw = await apiClient.patch<unknown>('/tenant/coaching', input);
  return CoachingSchema.parse(raw);
}

// ─── AI summary prompts (W4): the organisation's, on the server ────────────

const SummaryPromptSchema = z.object({
  id: z.string(),
  name: z.string(),
  instructions: z.string(),
  is_default: z.boolean(),
  is_active: z.boolean(),
  updated_at: z.string().nullable(),
});

const SummaryPromptListSchema = z.object({
  prompts: z.array(SummaryPromptSchema),
  active_prompt_id: z.string(),
  can_edit: z.boolean(),
});

export type SummaryPrompt = z.infer<typeof SummaryPromptSchema>;
export type SummaryPromptList = z.infer<typeof SummaryPromptListSchema>;

export async function listSummaryPrompts(opts?: { signal?: AbortSignal }): Promise<SummaryPromptList> {
  return SummaryPromptListSchema.parse(await apiClient.get<unknown>('/tenant/ai-summary-prompts', opts));
}

export async function createSummaryPrompt(input: { name: string; instructions: string }): Promise<SummaryPrompt> {
  return SummaryPromptSchema.parse(await apiClient.post<unknown>('/tenant/ai-summary-prompts', input));
}

export async function deleteSummaryPrompt(id: string): Promise<void> {
  await apiClient.delete<unknown>(`/tenant/ai-summary-prompts/${encodeURIComponent(id)}`);
}

export async function activateSummaryPrompt(promptId: string): Promise<SummaryPromptList> {
  return SummaryPromptListSchema.parse(await apiClient.put<unknown>('/tenant/ai-summary-prompts/active', { prompt_id: promptId }));
}

export interface TenantProfileUpdate {
  contact_name?: string;
  calendly_url?: string;
  swoft_crm_enabled?: boolean;
  swoft_crm_url?: string;
  swoft_crm_api_key?: string;
  swoft_crm_tenant_id?: string;
  // ADR-036: OAuth client id and inbound events secret (write-only).
  swoft_client_id?: string;
  swoft_events_secret?: string;
  // Opt-in: push every qualified call to the CRM without the caller's button.
  auto_send_call_activity?: boolean;
  active_crm_provider?: 'none' | 'hubspot' | 'swoft';
}

export async function updateTenantProfile(input: TenantProfileUpdate): Promise<void> {
  await apiClient.patch<unknown>('/tenant/profile', input);
}
